import Vue from "vue";
import Router from "vue-router";
import CategorySelection from "./views/CategorySelection";
import Favourites from "./views/Favourites";
import Page from "./views/Page.vue";
import Rest from "./views/Rest.vue";
import { bus } from "./main";
import store from "./store";
import { buildProductCarouselPath, buildProductDisplayHomesPath, buildProductDwellingsPath, buildProductMasterplanPath, buildProductPackagesPath, buildProductPagePath, buildProductTurnkeysPath } from "@/utils/livingRoute";

Vue.use(Router);

const MASTERPLAN_ACCESS_CODE_ENTITY_ID = "masterplan";

const buildRedirectLocation = (route, path, queryOmissions = []) => {
  const query = { ...route.query };
  queryOmissions.forEach((key) => {
    delete query[key];
  });

  return {
    path,
    query,
    hash: route.hash,
  };
};

const getLegacyDwellingType = (route) => {
  return route.params.dwellingType || route.query.dwellingType || "";
};

const redirectLegacyMasterplan = (route, suffix = "") => {
  const basePath = buildProductMasterplanPath(route.params.categoryId, route.params.pathway);
  return buildRedirectLocation(route, `${basePath}${suffix}`);
};

const redirectLegacyCarousel = (route, propertyId = "") => {
  return buildRedirectLocation(route, buildProductCarouselPath(route.params.categoryId, route.params.productType, propertyId));
};

const redirectLegacyDwellings = (route, dwellingId = "") => {
  return buildRedirectLocation(route, buildProductDwellingsPath(route.params.categoryId, getLegacyDwellingType(route), dwellingId), ["dwellingType"]);
};

const redirectLegacyTurnkeys = (route, propertyId = "") => {
  return buildRedirectLocation(route, buildProductTurnkeysPath(route.params.categoryId, route.params.productType, propertyId));
};

const redirectLegacyPackages = (route, packageId = "") => {
  return buildRedirectLocation(route, buildProductPackagesPath(route.params.categoryId, packageId));
};

const redirectLegacyDisplayHomes = (route, propertyId = "") => {
  return buildRedirectLocation(route, buildProductDisplayHomesPath(route.params.categoryId, propertyId));
};

const router = new Router({
  mode: "history",
  routes: [
    {
      name: "rest",
      path: "/",
      component: Rest,
    },
    {
      name: "category-selection",
      path: "/select",
      component: CategorySelection,
    },
    {
      name: "favourites",
      path: "/category/:categoryId/favourites",
      component: Favourites,
    },
    {
      name: "favourite-modal",
      path: "/category/:categoryId/favourites/:favouriteId",
      component: Favourites,
    },
    {
      name: "page",
      path: "/category/:categoryId/page/:pageId",
      component: Page,
    },
    {
      name: "product-living-entry",
      path: "/category/:categoryId/product/masterplan",
      component: Page,
    },
    {
      name: "product-living-entry-type",
      path: "/category/:categoryId/product/masterplan/:type(land|house|houses|townhome|townhomes|townhouse|townhouses|apartment|apartments|turnkeys)",
      component: Page,
    },
    {
      name: "product-living-option",
      path: "/category/:categoryId/product/masterplan/:livingOptionId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-living-option-turnkey-type",
      path: "/category/:categoryId/product/masterplan/turnkeys/:turnkeyType([^/]+)/:livingOptionId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-living-option-type",
      path: "/category/:categoryId/product/masterplan/:type(land|house|houses|townhome|townhomes|townhouse|townhouses|apartment|apartments|turnkeys)/:livingOptionId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-living-entry-turnkey-type",
      path: "/category/:categoryId/product/masterplan/turnkeys/:turnkeyType([^/]+)",
      component: Page,
    },
    {
      name: "product-subsection",
      path: "/category/:categoryId/product/masterplan/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-subsection-turnkey-type",
      path: "/category/:categoryId/product/masterplan/turnkeys/:turnkeyType([^/]+)/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-subsection-type",
      path: "/category/:categoryId/product/masterplan/:type(land|house|houses|townhome|townhomes|townhouse|townhouses|apartment|apartments|turnkeys)/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-property",
      path: "/category/:categoryId/product/masterplan/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})/property/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-property-turnkey-type",
      path: "/category/:categoryId/product/masterplan/turnkeys/:turnkeyType([^/]+)/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})/property/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "product-property-type",
      path: "/category/:categoryId/product/masterplan/:type(land|house|houses|townhome|townhomes|townhouse|townhouses|apartment|apartments|turnkeys)/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})/property/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "dwellings",
      path: "/category/:categoryId/product/carousel/dwellings",
      component: Page,
    },
    {
      name: "dwelling-modal",
      path: "/category/:categoryId/product/carousel/dwellings/:dwellingId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "dwelling-modal",
      path: "/category/:categoryId/product/carousel/dwellings/:dwellingType([^/]+)/:dwellingId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "dwellings",
      path: "/category/:categoryId/product/carousel/dwellings/:dwellingType([^/]+)",
      component: Page,
    },
    {
      path: "/category/:categoryId/product/carousel/dwelling",
      redirect: (route) => buildRedirectLocation(route, buildProductDwellingsPath(route.params.categoryId)),
    },
    {
      path: "/category/:categoryId/product/carousel/dwelling/:dwellingId([A-Fa-f\\d]{24})",
      redirect: (route) => buildRedirectLocation(route, buildProductDwellingsPath(route.params.categoryId, "", route.params.dwellingId)),
    },
    {
      path: "/category/:categoryId/product/carousel/dwelling/:dwellingType([^/]+)/:dwellingId([A-Fa-f\\d]{24})",
      redirect: (route) => buildRedirectLocation(route, buildProductDwellingsPath(route.params.categoryId, route.params.dwellingType, route.params.dwellingId)),
    },
    {
      path: "/category/:categoryId/product/carousel/dwelling/:dwellingType([^/]+)",
      redirect: (route) => buildRedirectLocation(route, buildProductDwellingsPath(route.params.categoryId, route.params.dwellingType)),
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/turnkeys",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/turnkeys/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/turnkeys/:productType([^/]+)/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/turnkeys/:productType([^/]+)",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/:productType(land|house|houses|townhome|townhomes|townhouse|townhouses|apartment|apartments)",
      component: Page,
    },
    {
      name: "carousel",
      path: "/category/:categoryId/product/carousel/:productType(land|house|houses|townhome|townhomes|townhouse|townhouses|apartment|apartments)/:propertyId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "packages",
      path: "/category/:categoryId/product/carousel/packages",
      component: Page,
    },
    {
      name: "package-modal",
      path: "/category/:categoryId/product/carousel/packages/:packageId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "package-modal",
      path: "/category/:categoryId/product/carousel/packages/:packageType([^/]+)/:packageId([A-Fa-f\\d]{24})",
      component: Page,
    },
    {
      name: "packages",
      path: "/category/:categoryId/product/carousel/packages/:packageType([^/]+)",
      component: Page,
    },
    {
      name: "legacy-live-entry",
      path: "/category/:categoryId/live",
      component: Page,
    },
    {
      path: "/category/:categoryId/live/land",
      redirect: (route) => buildRedirectLocation(route, buildProductMasterplanPath(route.params.categoryId)),
    },
    {
      name: "live-display-homes",
      path: "/category/:categoryId/product/displayhomes",
      component: Page,
    },
    {
      name: "live-display-homes-property",
      path: "/category/:categoryId/product/displayhomes/property/:propertyId",
      component: Page,
    },

    {
      name: "live-page",
      path: "/product/page/:pageId",
      component: Page,
    },
    {
      path: "/category/:categoryId/product/packages/:packageId([A-Fa-f\\d]{24})",
      redirect: (route) => buildRedirectLocation(route, buildProductPackagesPath(route.params.categoryId, route.params.packageId)),
    },
    {
      path: "/category/:categoryId/product/packages",
      redirect: (route) => buildRedirectLocation(route, buildProductPackagesPath(route.params.categoryId)),
    },
    {
      path: "/category/:categoryId/live/display-homes/property/:propertyId",
      redirect: (route) => redirectLegacyDisplayHomes(route, route.params.propertyId),
    },
    {
      path: "/category/:categoryId/live/display-homes",
      redirect: (route) => redirectLegacyDisplayHomes(route),
    },
    {
      path: "/live/page/:pageId",
      redirect: (route) => buildRedirectLocation(route, buildProductPagePath(route.params.pageId)),
    },
    {
      path: "/category/:categoryId/live/packages/:packageId",
      redirect: (route) => redirectLegacyPackages(route, route.params.packageId),
    },
    {
      path: "/category/:categoryId/live/packages",
      redirect: (route) => redirectLegacyPackages(route),
    },
    {
      path: "/category/:categoryId/live/dwellings/:dwellingId([A-Fa-f\\d]{24})",
      redirect: (route) => redirectLegacyDwellings(route, route.params.dwellingId),
    },
    {
      path: "/category/:categoryId/live/dwellings",
      redirect: (route) => redirectLegacyDwellings(route),
    },
    {
      path: "/category/:categoryId/live/turnkeys/:propertyId([A-Fa-f\\d]{24})",
      redirect: (route) => redirectLegacyTurnkeys(route, route.params.propertyId),
    },
    {
      path: "/category/:categoryId/live/turnkeys",
      redirect: (route) => redirectLegacyTurnkeys(route),
    },
    {
      path: "/category/:categoryId/live/carousel/:productType/:propertyId([A-Fa-f\\d]{24})",
      redirect: (route) => redirectLegacyCarousel(route, route.params.propertyId),
    },
    {
      path: "/category/:categoryId/live/carousel/:productType",
      redirect: (route) => redirectLegacyCarousel(route),
    },
    {
      path: "/category/:categoryId/live/carousel",
      redirect: (route) => redirectLegacyCarousel(route),
    },
    {
      path: "/category/:categoryId/live/:pathway/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})/property/:propertyId([A-Fa-f\\d]{24})",
      redirect: (route) => redirectLegacyMasterplan(route, `/${route.params.livingOptionId}/subsection/${route.params.subsectionId}/property/${route.params.propertyId}`),
    },
    {
      path: "/category/:categoryId/live/:pathway/:livingOptionId([A-Fa-f\\d]{24})/subsection/:subsectionId([A-Fa-f\\d]{24})",
      redirect: (route) => redirectLegacyMasterplan(route, `/${route.params.livingOptionId}/subsection/${route.params.subsectionId}`),
    },
    {
      path: "/category/:categoryId/live/:pathway/:livingOptionId([A-Fa-f\\d]{24})",
      redirect: (route) => redirectLegacyMasterplan(route, `/${route.params.livingOptionId}`),
    },
    {
      path: "/category/:categoryId/live/:pathway",
      redirect: (route) => redirectLegacyMasterplan(route),
    },
  ],
});

// TODO: route guard bottombar visiblilty - is there a better way of doing this?
// TODO: handle skipping category selection better
router.beforeEach((to, from, next) => {
  store.commit("clearAnimationQueue");

  const protectedEntity = getAccessCodeEntityFromRoute(to);
  if (shouldPromptForAccessCode(protectedEntity)) {
    store.commit("OPEN_ACCESS_CODE_PROMPT", {
      accessCode: protectedEntity.accessCode,
      entityId: protectedEntity._id,
      pendingRoute: {
        path: to.path,
        query: to.query,
        hash: to.hash,
      },
    });
    next(false);
    return;
  }

  // transition video route handling (supporting page, living options + subsections)
  const fromEntity = getEntityFromRoute(from);
  const toEntityId = getEntityIdFromRoute(to);
  if (fromEntity && fromEntity.pageTransitionVideos && toEntityId && fromEntity.pageTransitionVideos[toEntityId]) {
    store.state.activeTransitionBackground = fromEntity.pageTransitionVideos[toEntityId];
    store.commit("addAnimationToQueue", "BackgroundTransition");
  }

  if (to.path === "/select") {
    if (store.getters.navigation && store.getters.navigation.length === 1) {
      store.state.isBottomBarVisible = true;
      const category = store.getters.navigation[0];
      const defaultChildUrl = store.getters.navigation[0].defaultChildUrl;
      const isCustomRoute = store.getters.navigation[0].isCustomRoute;
      const customRoute = store.getters.navigation[0].customRoute;

      let newTo = "";

      if (isCustomRoute) {
        newTo = customRoute;
      } else if (defaultChildUrl) {
        newTo = defaultChildUrl;
      } else {
        newTo = `/category/${category._id}/page/${category.children[0].pageId}`;
      }

      if (from.path === newTo) {
        next(false);
      } else {
        next(newTo);
      }
    } else {
      store.state.isBottomBarVisible = false;
      next();
    }
  } else if (["/"].includes(to.path)) {
    store.state.isBottomBarVisible = false;
    next();
  } else {
    store.state.isBottomBarVisible = true;
    next();
  }
});

router.afterEach(() => {
  // store.commit('clearAnimationQueue')

  setTimeout(() => {
    bus.$emit("startAnimations");
  }, 50);
});

function getEntityIdFromRoute(route) {
  return route.params.pageId || route.params.subsectionId || route.params.livingOptionId || null;
}

function getEntityFromRoute(route) {
  const pageId = route.params.pageId;
  const subsectionId = route.params.subsectionId;
  const livingOptionId = route.params.livingOptionId;

  if (pageId) return getPageByPageId(pageId);
  if (subsectionId) return getSubsectionById(subsectionId);
  if (livingOptionId) return getLivingOptionById(livingOptionId);

  return null;
}

function getPageByPageId(pageId) {
  return store.state.pages.find((page) => page._id === pageId);
}

function getSubsectionById(subsectionId) {
  const subs = (store.state.property && store.state.property.subsections) || [];
  return subs.find((s) => s._id === subsectionId);
}

function getLivingOptionById(livingOptionId) {
  const living = (store.state.property && store.state.property.livingOptions) || [];
  return living.find((l) => l._id === livingOptionId);
}

export function shouldPromptForAccessCode(entity) {
  if (!entity || !entity._id || !entity.accessCode) return false;
  if (!String(entity.accessCode).trim()) return false;

  const unlockedAt = store.state.unlockedAccessCodeEntities[entity._id];
  if (!unlockedAt) return true;

  return Date.now() - unlockedAt > getAccessCodeTimeout();
}

export function getAccessCodeEntityFromRoute(route) {
  const entities = [
    getMasterplanAccessCodeEntityFromRoute(route),
    route.params.pageId ? getPageByPageId(route.params.pageId) : null,
    route.params.livingOptionId ? getLivingOptionById(route.params.livingOptionId) : null,
    route.params.subsectionId ? getSubsectionById(route.params.subsectionId) : null,
  ];

  return entities.find((entity) => shouldPromptForAccessCode(entity)) || null;
}

function getMasterplanAccessCodeEntityFromRoute(route) {
  if (!isMasterplanRoute(route)) return null;

  return {
    _id: MASTERPLAN_ACCESS_CODE_ENTITY_ID,
    accessCode: store.state.config && store.state.config.liveHereMasterplanAccessCode,
  };
}

function isMasterplanRoute(route) {
  return route && route.path && route.path.indexOf("/product/masterplan") !== -1;
}

function getAccessCodeTimeout() {
  const timeout = store.state.newConfig && store.state.newConfig.general && store.state.newConfig.general.accessCodeTimeout;
  const parsedTimeout = Number(timeout);

  return Number.isFinite(parsedTimeout) && parsedTimeout >= 0 ? parsedTimeout : 5 * 60 * 1000;
}

export default router;
